import { computed } from "vue";
import type { ShopexpertsService, ExpertListingRequest, ShopexpertPartnerBadge, ShopexpertsPreviewExpert } from "@shopexperts/shared";
import { useApi } from "@shopexperts/shared";
import { useQuery } from "@tanstack/vue-query";

import { useListingDetailsStore } from "./useListingDetailsStore";
import { useListingPackagedServicesStore } from "./useListingPackagedServicesStore";
import { useListingCustomerStoriesStore } from "./useListingCustomerStoriesStore";
import { useListingFaqsStore } from "./useListingFaqsStore";
import { generateStorySlug } from "../utils/slugGenerator";
import { transformProfileToApi } from "../utils/transformers/profileTransformers";
import useProfileDetails from "./useProfileDetails";

/**
 * Coordinator for cross-cutting concerns across all listing stores.
 * Use individual stores directly in components for specific data.
 * Use this coordinator for:
 * - Tracking changes across all sections
 * - Validating if ready to publish
 * - Coordinating save/reset across all stores
 * - Getting publishable data (only dirty sections)
 * - Getting preview data (all current data)
 */
export function useListingCoordinator() {
    const detailsStore = useListingDetailsStore();
    const packagedStore = useListingPackagedServicesStore();
    const storiesStore = useListingCustomerStoriesStore();
    const faqsStore = useListingFaqsStore();
    const { badges: badgesRaw, profile } = useProfileDetails();
    const api = useApi();
    const { data: categoriesWithSubcategories } = useQuery({
        queryKey: ["service-categories", "with-subs"],
        queryFn: () => api.categories.getCategoriesWithSubcategories(),
        staleTime: 30 * 60 * 1000,
        refetchOnWindowFocus: false,
    });
    const expertSlug = computed(() => profile.value?.slug || '');

    const changesList = computed(() => {
        const changes: string[] = [];
        if (detailsStore.isDirty.value) changes.push("Personal & Business Details");
        if (packagedStore.isDirty.value) changes.push("Packaged Services");
        if (storiesStore.isDirty.value) changes.push("Case Studies Order");
        if (faqsStore.isDirty.value) changes.push("FAQ");
        return changes;
    });

    const isValidToPublish = computed(() => {
        if (!detailsStore.isDirty.value && detailsStore.initial.value) {
            return true;
        }
        if (detailsStore.isDirty.value) {
            return detailsStore.isValid.value;
        }
        return true;
    });

    function markAllSaved() {
        detailsStore.markSaved();
        packagedStore.markSaved();
        storiesStore.markSaved();
        faqsStore.markSaved();
    }

    function resetAll() {
        detailsStore.resetAll();
        packagedStore.resetAll();
        storiesStore.resetAll();
        faqsStore.resetAll();
    }

    /**
     * Get publish payload - returns ONLY dirty sections for API update
     * Used when publishing changes to the backend
     * Returns Partial<ExpertListingRequest> with undefined for non-dirty sections
     */
    function getPublishPayload(): Partial<ExpertListingRequest> | null {
        const hasChanges = changesList.value.length > 0;
        if (!hasChanges) return null;

        const profileData = detailsStore.isDirty.value ? detailsStore.getPublishable(expertSlug.value) : undefined;
        if (profileData && !detailsStore.hasActiveProSubscription.value) {
            delete profileData.geo_regions;
            delete profileData.regions_served;
        }

        return {
            profile: profileData,
            packaged_services: packagedStore.isDirty.value ? packagedStore.getPublishable() : undefined,
            customer_stories: storiesStore.isDirty.value ? storiesStore.getPublishable() : undefined,
            faqs: faqsStore.isDirty.value ? faqsStore.getPublishable() : undefined,
        };
    }

    /**
     * Get preview data - returns complete ShopexpertsService with ALL current data
     * Used for preview to show full profile with all changes
     * Manually maps store types to ShopexpertsService display format
     */
    function getPreviewData(): ShopexpertsPreviewExpert | null {
        const profile = transformProfileToApi(detailsStore.model.value, expertSlug.value, true);
        
        if (!profile) return null;
        
        const detailsFormModel = detailsStore.model.value;
        const packagedServicesRaw = packagedStore.model.value;
        const customerStoriesRaw = storiesStore.model.value;
        const faqsRaw = faqsStore.model.value;
        const subcategoryLookup = new Map(
            (categoriesWithSubcategories.value || []).flatMap((category) =>
                category.subcategories.map((subcategory) => [
                    String(subcategory.id),
                    {
                        id: subcategory.id,
                        name: subcategory.name,
                        category: {
                            id: category.id,
                            name: category.name,
                        },
                    },
                ] as const)
            )
        );
        
        const packagedServices = packagedServicesRaw.map(p => ({
            id: p.id?.toString(),
            slug: p.slug || generateStorySlug(p.service_name),
            service_name: p.service_name,
            thumbnail: p.thumbnail?.url || '',
            gallery_images: p.gallery_images?.map(img => img.url).filter((url): url is string => !!url) || [],
            starting_price: p.starting_price,
            price_type: p.price_type,
            description: p.description,
        }));
        
        const customerStories = customerStoriesRaw.map(s => ({
            id: s.id ?? undefined,
            title: s.title,
            slug: s.slug || generateStorySlug(s.title),
            images: s.images?.map(img => img.url).filter((url): url is string => !!url) || [],
            content: s.content,
            hired_at_shopexperts: s.hired_at_shopexperts ?? undefined,
            start_date: s.start_date ?? undefined,
            end_date: s.end_date ?? undefined,
        }));
        
        const offeredServices = Object.values(
            (detailsFormModel?.subcategories || []).reduce((acc, subcategoryId) => {
                const selectedSubcategory = subcategoryLookup.get(subcategoryId);
                if (!selectedSubcategory) return acc;

                const categoryId = selectedSubcategory.category.id;
                if (!acc[categoryId]) {
                    acc[categoryId] = {
                        service_category_id: categoryId,
                        service_subcategory_ids: [],
                        meta: {
                            service_category: selectedSubcategory.category.name,
                            service_subcategories: [],
                        },
                    };
                }

                acc[categoryId]!.service_subcategory_ids.push(selectedSubcategory.id);
                acc[categoryId]!.meta.service_subcategories.push(selectedSubcategory.name);

                return acc;
            }, {} as Record<number, {
                service_category_id: number;
                service_subcategory_ids: number[];
                meta: {
                    service_category: string;
                    service_subcategories: string[];
                };
            }>)
        );
        
        const faqs = faqsRaw.map(f => ({
            id: f.id?.toString(),
            question: f.question,
            answer: f.answer,
        }));

        const badges = badgesRaw.value.map(b => ({
            logo: b.icon?.url || '',
            name: b.name,
        } as ShopexpertPartnerBadge));
        
        const previewProfile = {
            ...profile,
            slug: profile.slug || '',
            profile_picture: detailsFormModel?.avatar?.url || '', // Get URL directly from form
            representative_picture: profile.expert_type === 'agency' && 'representative_picture_media_id' in profile 
                ? (detailsFormModel && 'representativePicture' in detailsFormModel ? detailsFormModel.representativePicture?.url : undefined)
                : undefined,
            expert_type: profile.expert_type,
        } as ShopexpertsService['profile'];
        
        console.log(`[slug]`, profile.slug);
        return {
            profile: previewProfile,
            offered_services: offeredServices,
            packaged_services: packagedServices,
            customer_stories: customerStories,
            faqs: faqs,
            badges: badges,
        };
    }

    return {
        changesList,
        isValidToPublish,
        markAllSaved,
        resetAll,
        getPublishPayload,
        getPreviewData,
    };
}

export default useListingCoordinator;
